import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	CancellationReasonPassengerBookingEnum,
	PassengerBookingView,
} from '@mopla/data-models';
import {
	findCancellationFee,
	getItineraryTimingV2,
	isSpecialSchoolTrafficItinerary,
} from '@mopla/utils';

import { MplButton } from '../../../atoms/MplButton/MplButton';
import { MplErrorModal } from '../../MplModal/MplErrorModal';

import { CancellationConfirmationModalV2 } from './CancellationConfirmationModalV2';
import { CancellationReasonOverlayV2 } from './CancellationReasonOverlayV2';
import { CancellationSuccessModalV2 } from './CancellationSuccessModalV2';
import { SchoolConfirmationModal } from './SchoolConfirmationModal';
import { ICancellationReasonFormState, TCancellationStep } from './types';
import { useCancelBookingRequest } from './useCancelBookingRequest';

interface ICancellationFlowV2Props {
	bookedItinerary: PassengerBookingView;
	onClose: VoidFunction;
}

export const CancellationFlowV2: FC<ICancellationFlowV2Props> = ({
	bookedItinerary,
	onClose,
}) => {
	const [step, setStep] = useState<TCancellationStep>('confirmation');
	const { cancelBooking, isCancelling } = useCancelBookingRequest();
	const { t } = useTranslation('errors');

	const cancellationFee = useMemo(
		() => findCancellationFee(bookedItinerary.itinerary),
		[bookedItinerary]
	);
	const hasCancellationFee = cancellationFee > 0;

	const withDelayReason = useMemo(
		() => getItineraryTimingV2(bookedItinerary.itinerary.legs).isDelayed,
		[bookedItinerary]
	);

	const isSchool = useMemo(
		() => isSpecialSchoolTrafficItinerary(bookedItinerary.itinerary),
		[bookedItinerary]
	);

	const onSubmit = useCallback(
		async ({ reason, otherReasonText }: ICancellationReasonFormState) => {
			const success = await cancelBooking({
				bookingNumber: bookedItinerary.bookingNumber,
				cancellationReason: reason,
				cancellationReasonDescription: otherReasonText || undefined,
			});
			setStep(success ? 'success' : 'error');
		},
		[cancelBooking, bookedItinerary]
	);

	/** School rides skip the reason form entirely, so we submit directly with
	 *  OTHER_REASON instead of going through the form (which would require a
	 *  description for OTHER_REASON). */
	const schoolCancelHandler = useCallback(async () => {
		const success = await cancelBooking({
			bookingNumber: bookedItinerary.bookingNumber,
			cancellationReason: CancellationReasonPassengerBookingEnum.OTHER_REASON,
		});
		setStep(success ? 'success' : 'error');
	}, [cancelBooking, bookedItinerary]);

	return (
		<>
			{isSchool ? (
				<SchoolConfirmationModal
					open={step === 'confirmation'}
					onClose={onClose}
					onConfirm={schoolCancelHandler}
					isCanceling={isCancelling}
				/>
			) : (
				<CancellationConfirmationModalV2
					open={step === 'confirmation'}
					onClose={onClose}
					onConfirm={() => setStep('reason')}
					price={cancellationFee}
				/>
			)}
			<CancellationReasonOverlayV2
				open={step === 'reason'}
				onClose={onClose}
				withDelayReason={withDelayReason}
				hasCancellationFee={hasCancellationFee}
				onSubmit={onSubmit}
				isCancelling={isCancelling}
			/>
			<CancellationSuccessModalV2
				open={step === 'success'}
				hasCancellationFee={hasCancellationFee}
				onClose={onClose}
			/>
			<MplErrorModal
				open={step === 'error'}
				testId="cancellationFlowV2-error-modal"
				buttons={
					<MplButton fullWidth onClick={onClose}>
						{t('button.close')}
					</MplButton>
				}
			/>
		</>
	);
};
